import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import DegreeApprenticeshipEditorialFooter from './DegreeApprenticeshipEditorialFooter';
import './DegreeApprenticeshipLandingPage.css';

// Reusable horizontal scroll-carousel shell (matches the swipeable card
// pattern used across the assessment matrix, method cards, sample
// question and employer/school case-study sections).
function ScrollCarousel({ children, ariaLabel, trackClassName }) {
  const scrollRef = useRef(null);
  const [dotIndex, setDotIndex] = useState(0);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const count = React.Children.count(children);

  const cardStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.firstChild || !el.firstChild.firstElementChild) return 0;
    const card = el.firstChild.firstElementChild;
    return card.getBoundingClientRect().width + 14;
  }, []);

  const updateUI = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const step = cardStep();
    const activeIndex = step ? Math.round(scrollLeft / step) : 0;
    setDotIndex(activeIndex);
    setShowPrev(scrollLeft > 4);
    setShowNext(scrollLeft < maxScroll - 4);
  }, [cardStep]);

  useEffect(() => {
    updateUI();
    const el = scrollRef.current;
    if (!el) return undefined;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateUI(); ticking = false; });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateUI);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateUI);
    };
  }, [updateUI]);

  const scrollByStep = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * cardStep(), behavior: 'smooth' });
  };

  return (
    <div className="degreeapprenticeship-scroll-carousel">
      <button
        type="button"
        className={`degreeapprenticeship-carousel-arrow degreeapprenticeship-arrow-prev ${showPrev ? 'show' : ''}`}
        onClick={() => scrollByStep(-1)}
        aria-label={`Previous ${ariaLabel}`}
      >
        ←
      </button>
      <button
        type="button"
        className={`degreeapprenticeship-carousel-arrow degreeapprenticeship-arrow-next ${showNext ? 'show' : ''}`}
        onClick={() => scrollByStep(1)}
        aria-label={`Next ${ariaLabel}`}
      >
        →
      </button>
      <div className="degreeapprenticeship-card-scroll" ref={scrollRef}>
        <div className={`degreeapprenticeship-card-track ${trackClassName || ''}`}>
          {children}
        </div>
      </div>
      <div className="degreeapprenticeship-swipe-dots">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className={i === dotIndex ? 'active' : ''} />
        ))}
      </div>
    </div>
  );
}

export default function DegreeApprenticeshipLandingPage() {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const assessmentFormats = [
    { programme: "KPMG Accountancy", stages: "Reasoning tests, video interview, assessment centre", format: "Group case study & partner interview", focus: "Commercial awareness, numerical reasoning, ethics under pressure" },
    { programme: "CMS Solicitor (SQE)", stages: "Situational judgement test, video interview, assessment centre", format: "Legal case study & partner interview", focus: "Commercial legal reasoning, client judgement, contract risk" },
    { programme: "JPMorgan Investment Banking", stages: "Numerical & logical tests, video interview, Superday", format: "Technical & competency interviews", focus: "Market awareness, quantitative reasoning, composure under pressure" },
    { programme: "IBM Software Engineering", stages: "Online coding test, video interview, assessment centre", format: "Live coding & technical interview", focus: "Algorithmic problem-solving, system design, collaborative debugging" },
    { programme: "Rolls-Royce Engineering", stages: "Reasoning tests, video interview, assessment centre", format: "Technical exercise & panel interview", focus: "Estimation, safety-critical trade-offs, technical communication" },
    { programme: "PwC Actuarial", stages: "Numerical reasoning, video interview, assessment centre", format: "Case study & technical interview", focus: "Statistical reasoning, risk modelling, business application" },
    { programme: "Met Police (PCDA)", stages: "Situational judgement test, values-based interview, assessment centre", format: "Role-play scenarios & written exercise", focus: "De-escalation judgement, procedural fairness, community reasoning" }
  ];

  const structureCards = [
    {
      tag: "Method",
      title: "The IVORI Method",
      meta: "Interact with Pedagogical AI",
      items: [
        "Trained across three foundations spanning critical-incident interview research, assessment-centre validity science and naturalistic decision-making theory, so it assesses a live candidate the way a real assessor panel actually would",
        "Finds exactly where a competency answer or a piece of commercial reasoning stops holding up under follow-up, so you correct the actual gap, not just the final answer",
        <>The world's first <button type="button" className="degreeapprenticeship-inline-link degreeapprenticeship-link-btn" onClick={() => navigate('/method/degreeapprenticeship')}>pedagogical AI model</button>, built on the first <a href="https://ivorilab.com" target="_blank" rel="noopener noreferrer" className="degreeapprenticeship-inline-link">Foundation Decision World Model</a>, meaning the feedback comes from a system built for reasoning</>
      ]
    },
    {
      tag: "Behind Foundation II",
      title: "Assessment-Centre Validity Research",
      meta: "What Group Exercises Actually Predict",
      items: [
        "A landmark meta-analysis of decades of assessment-centre data found group and case exercises genuinely predict later job performance",
        "The same structured scoring dimensions IVORI applies to a group case discussion",
        "Documented across the same assessment-centre research employers themselves rely on to validate their own hiring"
      ]
    },
    {
      tag: "Behind Foundation III",
      title: "Naturalistic Decision-Making Research",
      meta: "Reasoning Under Incomplete Information",
      items: [
        "Research on how experienced professionals actually decide when facts are incomplete and still changing",
        "The exact skill an assessor tests by dropping new information into a live case",
        "Drawn from studying real decision-makers under time pressure, not laboratory puzzles with one clean answer"
      ]
    },
    {
      tag: "Behind Foundation I",
      title: "Critical-Incident Interview Research",
      meta: "The Origin of the STAR Framework",
      items: [
        "The Critical Incident Technique treats a specific, structured account of a real past situation as genuine evidence, not a rehearsed story",
        "The same principle behind every STAR-scored competency answer IVORI drills",
        "A structured example reveals a candidate's actual judgement a vague generality can't show"
      ]
    },
    {
      tag: "Three Skills, One Discipline",
      title: "Three Skills, Three Traditions",
      meta: "Trained on All Three Deliberately",
      items: [
        "Structured Answers is trained on critical-incident interview research — scoring the specific example you build, not only the competency you claim",
        "Group Exercise Composure is trained on assessment-centre validity research — scoring how you build on the room's thinking, exactly as a real assessor scores contribution",
        "Commercial Judgement is trained on naturalistic decision-making research — updating a recommendation the instant new information changes the case"
      ]
    },
    {
      tag: "Foundation I",
      title: "Structured Answers",
      meta: "Competency Interviews & the STAR Framework",
      items: [
        "Employers score competency answers against situation, task, action and result, not eloquence",
        "AI probes for a specific example, sentence by sentence, the way an actual assessor would",
        "Structured feedback on whether your answer actually proves the competency being tested"
      ]
    },
    {
      tag: "Foundation II",
      title: "Group Exercise Composure",
      meta: "Timed Group Case Discussions",
      items: [
        "Assessment centres score how you build on others' ideas, not just how much you talk",
        "Practice adding structure to a group discussion under a hard time limit",
        "Learn to recover a derailed discussion without steamrolling the room"
      ]
    },
    {
      tag: "Foundation III",
      title: "Commercial Judgement",
      meta: "New Information Mid-Case",
      items: [
        "Assessors introduce a twist mid-case specifically to see whether your recommendation actually updates",
        "Drills reward genuine adaptation over a rehearsed, pre-loaded answer",
        "Feedback shows exactly where your reasoning stayed static when the case changed under you"
      ]
    }
  ];

  const schoolCaseStudies = [
    {
      name: "Handsworth Grammar",
      contact: "Handsworth Grammar · Careers & Enrichment Team",
      body: "King Edward VI Handsworth Grammar School for Boys has used IVORI for almost two years, supporting Year 12 and 13 students heading toward degree apprenticeships in accountancy, engineering and law alongside its traditional university-track cohort.",
      slug: "handsworth-grammar",
      detailLabel: "Partner Since",
      detailValue: "May 2024",
      detail2Label: "License",
      detail2Value: "Annual"
    },
    {
      name: "Clifton College",
      contact: "Clifton's Team · Careers Department",
      body: "Piloted IVORI's degree apprenticeship simulator with a Year 13 cohort applying alongside their UCAS choices, rehearsing competency interviews and group exercises for candidates shortlisted at KPMG, CMS and JPMorgan.",
      slug: "clifton-college",
      detailLabel: "Cohort",
      detailValue: "Year 13",
      detail2Label: "Focus",
      detail2Value: "Assessment-Centre Readiness"
    },
    {
      name: "King Edward VI",
      contact: "Active Discussions",
      body: "Building on the existing relationship with Handsworth Grammar, IVORI is in active discussions with the wider King Edward VI Foundation about extending degree apprenticeship assessment-centre prep across its family of Birmingham grammar schools.",
      slug: "king-edward-vi-foundation",
      detailLabel: "Status",
      detailValue: "In Discussion",
      detail2Label: "Scope",
      detail2Value: "Foundation-Wide"
    },
    {
      name: "Grammar Schools",
      contact: "School Group",
      body: "Deployed across schools within the grammar school group for degree apprenticeship assessment-centre practice, supporting a strong academic cohort increasingly weighing a leading employer scheme against a university offer.",
      slug: "grammar-schools",
      detailLabel: "Group",
      detailValue: "State Grammar Schools",
      detail2Label: "Focus",
      detail2Value: "Assessment-Centre Prep"
    },
    {
      name: "Independent Schools",
      contact: "School Group",
      body: "Deployed across schools within the independent school group for degree apprenticeship assessment-centre practice, supporting a small but growing cohort choosing a flagship employer scheme alongside the traditional university track.",
      slug: "independent-schools",
      detailLabel: "Group",
      detailValue: "Independent Schools",
      detail2Label: "Focus",
      detail2Value: "Assessment-Centre Prep"
    },
    {
      name: "Sixth-Form & FE Colleges",
      contact: "School Group",
      body: "Deployed across sixth-form and further-education colleges for degree apprenticeship practice, supporting students across all seven flagship programmes — often the first structured assessment-centre coaching these students have had access to.",
      slug: "sixth-form-colleges",
      detailLabel: "Group",
      detailValue: "Sixth-Form & FE Colleges",
      detail2Label: "Focus",
      detail2Value: "Degree Apprenticeship Prep"
    }
  ];

  const faqs = [
    {
      q: "What is a degree apprenticeship assessment centre, and how is it different from a normal job interview?",
      a: [
        "A degree apprenticeship combines a funded university degree with paid, full-time work at a real employer from day one. Recruitment usually runs online reasoning and situational-judgement tests, then a recorded video interview, then an in-person or virtual assessment centre with a group exercise, case study and panel interview.",
        "Because candidates are being assessed for a live, multi-year role rather than just a place on a course, employers weight commercial judgement and workplace competencies more heavily than a purely academic interview would — exactly what IVORI's degree apprenticeship assessment-centre prep and tutoring is built to train, rather than a bank of questions with a single correct answer."
      ]
    },
    {
      q: "What is IVORI's pedagogical method for degree apprenticeship prep, and who's behind it?",
      a: [
        "Every simulation, follow-up and feedback pattern is reviewed by admissions and assessment-centre specialists, alongside educators with formal pedagogical training. That review process is what lets IVORI explain not just whether an answer held up, but exactly where it stopped holding up.",
        "IVORI trains on three research traditions, one per assessment skill: the Critical Incident Technique for structured competency answers, assessment-centre validity research for group-exercise composure, and naturalistic decision-making research for commercial judgement under live pressure. Underneath all three sits IVORI's own foundation decision world model — proprietary technology researched and pioneered in-house by IVORI Lab, and, we believe, the world's first pedagogical AI model built on this kind of foundation.",
        "It's IVORI Lab's proprietary foundation decision world model — a system trained to reason about decision-making under uncertainty, rather than simply predict text. IVORI's degree apprenticeship products sit on top of this underlying model, using its proprietary training methodology to run live follow-up pressure and score a candidate's reasoning the way a real assessor would."
      ]
    },
    {
      q: "Which programmes does IVORI's degree apprenticeship simulator cover?",
      a: ["IVORI covers assessment-centre style simulations across seven of the UK's most in-demand degree apprenticeship pathways — modelled on the recruitment style at KPMG (Big 4 Accountancy), CMS (Solicitor Apprenticeship / SQE), JPMorgan (Investment Banking & Finance), IBM (Software Engineering), Rolls-Royce (Engineering), PwC (Actuarial) and the Metropolitan Police (Police Constable / PCDA), alongside general competency and situational-judgement practice used across employers such as PwC, EY, Deloitte, Barclays, Goldman Sachs, BT, Amazon, BAE Systems, Aon and Zurich."]
    },
    {
      q: "How do IVORI's assessment-centre mocks work?",
      a: [
        "Each simulation opens with a real assessment-style question — a competency prompt, case study or technical scenario — then applies live follow-up pressure the way an actual assessor would: challenging your weakest point, or introducing new information mid-answer.",
        "You receive structured feedback on answer structure, commercial judgement, and how well you adapted your position, so you know exactly which of the three underlying skills still needs work before the real assessment centre."
      ]
    },
    {
      q: "Can I cancel my subscription at any time?",
      a: ["Yes. All subscriptions can be managed or cancelled in one click from your account profile with zero hidden fees or commitments."]
    }
  ];

  const host = window.location.hostname.toLowerCase();
  const onIvoriEdu = host === 'degreeapprenticeship.ivoriedu.com';
  const onIvoriAiEdu = host === 'degreeapprenticeship.edu.ivoriai.com';
  const daUrl = onIvoriEdu
    ? 'https://degreeapprenticeship.ivoriedu.com/'
    : onIvoriAiEdu
      ? 'https://degreeapprenticeship.edu.ivoriai.com/'
      : (host.includes('degreeapprenticeship') ? 'https://degreeapprenticeship.edu.ivoriai.com/' : 'https://ivoriai.com/edu/degreeapprenticeship');
  const eduHomeUrl = onIvoriEdu
    ? 'https://ivoriedu.com'
    : 'https://ivoriai.com/';

  return (
    <div className="degreeapprenticeship-landing-root">
      <SeoMeta
        title="IVORI — Degree Apprenticeship Assessment Centre Prep"
        description="AI-powered degree apprenticeship assessment-centre prep — video interviews, group exercises and employer mocks from IVORI Edu."
        keywords="degree apprenticeship prep, assessment centre prep, kpmg assessment centre, cms solicitor apprenticeship, jpmorgan superday prep, ibm software engineering interview, rolls-royce engineering assessment, pwc actuarial assessment, met police pcda prep, ivori degree apprenticeship, degree apprenticeship numerical reasoning, situational judgment test apprenticeship, commercial awareness questions, corporate assessment centre exercises"
        url={daUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Course",
                "name": "Degree Apprenticeship Assessment Centre Preparation",
                "description": "AI-powered degree apprenticeship assessment-centre prep from IVORI Edu, trained on three foundations spanning critical-incident interview research, assessment-centre validity meta-analysis and naturalistic decision-making science — live competency interviews, group-exercise drilling and employer-specific technical mocks across KPMG, CMS, JPMorgan, IBM, Rolls-Royce, PwC and the Metropolitan Police.",
                "provider": {
                  "@type": "Organization",
                  "name": "ivori",
                  "url": eduHomeUrl
                },
                "url": daUrl,
                "courseMode": "online",
                "inLanguage": "en-GB",
                "isAccessibleForFree": false,
                "educationalLevel": "A-Level / Undergraduate Admissions",
                "teaches": [
                  "Structured competency answers using the STAR framework",
                  "Group-exercise composure under timed collaborative pressure",
                  "Commercial judgement and live case-update reasoning",
                  "Employer-specific technical and situational-judgement practice across seven flagship programmes"
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": faqs.map((f) => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a.join(' ') }
                }))
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ivoriai.com" },
                  { "@type": "ListItem", "position": 2, "name": "Degree Apprenticeship Assessment Centre Preparation", "item": daUrl }
                ]
              }
            ]
          })
        }}
      />
      <Header variant="exam-landing" />

      {/* HERO */}
      <section className="degreeapprenticeship-hero-section">
        <div className="degreeapprenticeship-container degreeapprenticeship-hero-content">
          <span className="degreeapprenticeship-mono-eyebrow">IVORI Edu</span>
          <h1 className="degreeapprenticeship-hero-title">Master the Degree Apprenticeship Assessment Centre.</h1>
          <p className="degreeapprenticeship-hero-stat">Assessment-centre prep across the UK's seven most in-demand degree apprenticeship employers, from Big 4 accountancy to investment banking, engineering and policing.</p>
          <p className="degreeapprenticeship-hero-tagline">Train the structured reasoning employers actually score.</p>
          <button type="button" className="degreeapprenticeship-hero-cta" onClick={() => navigate('/pricing?package=degree_apprenticeship')}>See plans</button>
        </div>
      </section>

      {/* ASSESSMENT FORMAT MATRIX */}
      <section id="degreeapprenticeship-matrix" className="degreeapprenticeship-editorial-section">
        <div className="degreeapprenticeship-container">
          <div className="degreeapprenticeship-editorial-section-head">
            <span className="degreeapprenticeship-mono-eyebrow">Employer-By-Employer Breakdown</span>
            <h2>Degree Apprenticeship Assessment Formats</h2>
            <p>KPMG accountancy assessment-centre prep, CMS solicitor (SQE) assessment-centre prep, JPMorgan investment banking assessment-centre prep, IBM software engineering assessment-centre prep, Rolls-Royce engineering assessment-centre prep, PwC actuarial assessment-centre prep and Met Police (PCDA) assessment-centre prep — what to expect at assessment across the UK's seven most in-demand degree apprenticeship employers.</p>
          </div>

          <ScrollCarousel ariaLabel="programme">
            {assessmentFormats.map((row, idx) => (
              <div className="degreeapprenticeship-matrix-card" key={idx}>
                <div className="degreeapprenticeship-matrix-uni">{row.programme}</div>
                <div className="degreeapprenticeship-matrix-row"><span>Assessment Stages</span><b>{row.stages}</b></div>
                <div className="degreeapprenticeship-matrix-row"><span>Format</span><b>{row.format}</b></div>
                <div className="degreeapprenticeship-matrix-row degreeapprenticeship-score-cell"><span>Primary Focus</span><b>{row.focus}</b></div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* THE IVORI METHOD */}
      <section className="degreeapprenticeship-editorial-section">
        <div className="degreeapprenticeship-container">
          <div className="degreeapprenticeship-editorial-section-head">
            <span className="degreeapprenticeship-mono-eyebrow">How It Works</span>
            <h2>The IVORI Method</h2>
            <p>Three skills a CV and predicted grades never test on paper — and the three an assessment centre is actually built around</p>
          </div>

          <ScrollCarousel ariaLabel="skill">
            {structureCards.map((card, idx) => (
              <div className="degreeapprenticeship-structure-card" key={idx}>
                <span className="degreeapprenticeship-mono-tag">{card.tag}</span>
                <h3>{card.title}</h3>
                <div className="degreeapprenticeship-structure-meta">{card.meta}</div>
                <ul>
                  {card.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* SAMPLE INTERVIEW */}
      <section className="degreeapprenticeship-editorial-section">
        <div className="degreeapprenticeship-container">
          <div className="degreeapprenticeship-editorial-section-head">
            <span className="degreeapprenticeship-mono-eyebrow">Interactive Practice</span>
            <h2>Improve Your Score</h2>
            <p>See how IVORI runs a live video interview and assessment-centre exercise across all seven flagship programmes — including the follow-up pressure a real assessor would apply</p>
          </div>

          <ScrollCarousel ariaLabel="card">
            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">KPMG Accountancy · Opening Question</span>
              <div className="degreeapprenticeship-passage">
                "A client's finance director asks you to hold off recognizing a supplier invoice until next quarter, so this quarter's numbers hit target."
              </div>
              <h3>How do you respond, and what do you do next?</h3>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">Follow-Up Pressure</span>
              <div className="degreeapprenticeship-option">What if the FD says every client does this and it's not material to the accounts?</div>
              <div className="degreeapprenticeship-option">Does your answer change if you're the only junior team member in the room?</div>
              <div className="degreeapprenticeship-option">What would you actually put in writing afterwards, and to whom?</div>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">What IVORI Listens For</span>
              <p>Strong answers name the request as a threat to objectivity and professional scepticism, not just "wrong" — and don't wait for materiality to be the deciding factor.</p>
              <p>IVORI checks whether the candidate escalates through the correct channel rather than personally refusing and leaving it there, and whether they'd document the conversation.</p>
              <p>This is the same ethical-pressure reasoning KPMG, PwC, EY and Deloitte all build into their accountancy apprenticeship assessment centres.</p>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">CMS Solicitor (SQE) · Opening Question</span>
              <div className="degreeapprenticeship-passage">
                "A commercial client wants to terminate a five-year supply contract early because a cheaper supplier has appeared, but the contract has no early-termination clause."
              </div>
              <h3>What do you advise the client to do?</h3>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">Live Update — New Information</span>
              <div className="degreeapprenticeship-passage">
                "Suppose the existing supplier has already missed two delivery deadlines this year. Does that change your advice?"
              </div>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">What IVORI Listens For</span>
              <p>A rehearsed answer defends the original "you'll likely be in breach" position regardless of the new fact. A strong candidate re-examines whether the existing supplier is already in repudiatory breach, which could open a lawful exit.</p>
              <p>IVORI flags answers that ignore the interjection, and rewards candidates who separate the legal risk from the commercial recommendation explicitly.</p>
              <p>This is the same commercial-legal reasoning tested across Magic and Silver Circle solicitor apprenticeship assessment centres, not just at CMS.</p>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">JPMorgan Investment Banking · Opening Question</span>
              <div className="degreeapprenticeship-passage">
                "The Bank of England raises interest rates by 0.5 percentage points, unexpectedly."
              </div>
              <h3>Walk me through how this affects a typical listed company's share price.</h3>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">Follow-Up Pressure</span>
              <div className="degreeapprenticeship-option">Does your answer change for a highly leveraged company versus a debt-free one?</div>
              <div className="degreeapprenticeship-option">What if the company's earnings are mostly in a foreign currency?</div>
              <div className="degreeapprenticeship-option">Which effect dominates in the short term — the discount rate or the growth outlook?</div>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates connect higher rates to a higher discount rate in a DCF, lowering the present value of future cash flows — rather than reciting "rates up, stocks down" without a mechanism.</p>
              <p>IVORI checks whether leverage, currency exposure and time horizon are used to genuinely adjust the answer, not just mentioned in passing.</p>
              <p>This is the same first-principles quantitative reasoning JPMorgan, Barclays and Goldman Sachs all probe for in a technical apprenticeship interview.</p>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">IBM Software Engineering · Opening Question</span>
              <div className="degreeapprenticeship-passage">
                "You're given two large customer databases from a recent company merger, each with its own ID system and inconsistent formatting."
              </div>
              <h3>How would you design a system to detect duplicate customer records across both?</h3>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">Follow-Up Pressure</span>
              <div className="degreeapprenticeship-option">What if names are spelled slightly differently across the two systems?</div>
              <div className="degreeapprenticeship-option">How would your approach scale to 50 million records instead of 50 thousand?</div>
              <div className="degreeapprenticeship-option">How do you avoid merging two different people who happen to share an address?</div>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates move past exact-string matching to fuzzy matching on multiple fields with a confidence score, rather than treating this as a simple equality check.</p>
              <p>IVORI checks whether the candidate genuinely adapts the design when scale changes — indexing and blocking strategies instead of a naive all-pairs comparison — and reasons explicitly about false-positive risk.</p>
              <p>This scale-and-edge-case reasoning is exactly what IBM, BT and Amazon apprenticeship technical interviews are built to surface.</p>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">Rolls-Royce Engineering · Opening Question</span>
              <div className="degreeapprenticeship-passage">
                "You're asked to estimate the maximum bending load a simply supported aircraft wing spar can carry before failure, given its dimensions and material."
              </div>
              <h3>Walk through your approach, including which physical principles you'd apply.</h3>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">Follow-Up Pressure</span>
              <div className="degreeapprenticeship-option">How would your estimate change if the load were applied off-centre instead of evenly?</div>
              <div className="degreeapprenticeship-option">What assumptions are you making about the material, and how would you validate them?</div>
              <div className="degreeapprenticeship-option">What's the cost of being wrong here, and how does that change how conservative you'd be?</div>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates identify bending stress and moment of inertia as the governing quantities, and state their assumptions up front rather than jumping straight to a formula.</p>
              <p>IVORI checks whether the candidate treats this as safety-critical — building in a margin of safety and explaining why — rather than optimizing purely for the "correct" number.</p>
              <p>This estimation-under-stated-assumptions approach is exactly what Rolls-Royce and BAE Systems engineering apprenticeship assessment centres are testing for.</p>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">PwC Actuarial · Opening Question</span>
              <div className="degreeapprenticeship-passage">
                "An insurer's motor claims have risen 20% year on year, but premiums haven't moved."
              </div>
              <h3>Walk me through how you'd model the impact on the insurer's reserves.</h3>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">Follow-Up Pressure</span>
              <div className="degreeapprenticeship-option">How would you distinguish a genuine trend from one bad year of claims?</div>
              <div className="degreeapprenticeship-option">What if inflation is driving repair costs up, not claim frequency?</div>
              <div className="degreeapprenticeship-option">How would you communicate this risk to a non-technical board member?</div>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates separate frequency from severity as distinct drivers of the 20% rise, rather than treating "claims are up" as one undifferentiated number.</p>
              <p>IVORI checks whether the candidate proposes a way to test for genuine trend versus noise, and whether their final explanation is understandable to a non-actuarial audience.</p>
              <p>Translating a statistical model into a business recommendation is exactly what PwC, Aon and Zurich actuarial apprenticeship interviews are built around.</p>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">Met Police (PCDA) · Opening Question</span>
              <div className="degreeapprenticeship-passage">
                "You're called to a dispute between neighbours that has been reported five times this month, with no clear victim each time."
              </div>
              <h3>What's your first action on arrival?</h3>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">Follow-Up Pressure</span>
              <div className="degreeapprenticeship-option">One neighbour becomes verbally aggressive toward you specifically — what do you do?</div>
              <div className="degreeapprenticeship-option">What if a child is present in the household during the visit?</div>
              <div className="degreeapprenticeship-option">How do you decide whether this needs a referral beyond policing?</div>
            </div>

            <div className="degreeapprenticeship-qcard-panel">
              <span className="degreeapprenticeship-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates separate the individuals involved first, de-escalate before investigating, and treat repeat callouts as a signal to look for an underlying cause rather than repeating the same response each time.</p>
              <p>IVORI checks whether the candidate stays procedurally fair to both parties under provocation, and recognizes when a case needs a multi-agency referral rather than a purely policing response.</p>
              <p>This values-based, community-focused judgement is what Police Constable Degree Apprenticeship assessment centres are built to test, across every force in England and Wales.</p>
            </div>
          </ScrollCarousel>
        </div>
      </section>

      {/* SCHOOL CASE STUDIES */}
      <section className="degreeapprenticeship-editorial-section">
        <div className="degreeapprenticeship-container">
          <div className="degreeapprenticeship-editorial-section-head">
            <span className="degreeapprenticeship-mono-eyebrow">Used in Schools</span>
            <h2>Trusted by UK Schools &amp; Colleges</h2>
            <p>IVORI Edu's degree apprenticeship assessment-centre coaching runs from Handsworth Grammar degree apprenticeship prep and a Clifton College degree apprenticeship pilot, to sixth-form and FE college degree apprenticeship practice nationwide.</p>
          </div>

          <ScrollCarousel ariaLabel="school">
            {schoolCaseStudies.map((school, idx) => (
              <div className="degreeapprenticeship-school-card" key={idx}>
                <h3>{school.name}</h3>
                <div className="degreeapprenticeship-school-contact">{school.contact}</div>
                <p>{school.body}</p>
                <p className="degreeapprenticeship-school-more">
                  Find out more about{' '}
                  <button type="button" className="degreeapprenticeship-inline-link degreeapprenticeship-link-btn" onClick={() => navigate(`/case-studies/${school.slug}`)}>
                    {school.name}
                  </button>
                </p>
                <div className="degreeapprenticeship-school-detail">
                  {school.detailLabel}: <b>{school.detailValue}</b> · {school.detail2Label}: <b>{school.detail2Value}</b>
                </div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* FAQ */}
      <section className="degreeapprenticeship-editorial-section degreeapprenticeship-faq-editorial">
        <div className="degreeapprenticeship-container">
          <div className="degreeapprenticeship-editorial-section-head">
            <span className="degreeapprenticeship-mono-eyebrow">Got Questions?</span>
            <h2>FAQs</h2>
            <p>Find answers on prep tiers, mock formats and how IVORI's live feedback works</p>
          </div>

          <div className="degreeapprenticeship-faq">
            {faqs.map((faq, index) => (
              <div key={index} className={`degreeapprenticeship-faq-item ${activeFaq === index ? 'open' : ''}`}>
                <div className="degreeapprenticeship-faq-q" onClick={() => toggleFaq(index)}>
                  <span>{faq.q}</span>
                  <span className="degreeapprenticeship-faq-plus">+</span>
                </div>
                <div className="degreeapprenticeship-faq-a">
                  {faq.a.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <DegreeApprenticeshipEditorialFooter />
      <ScrollToTopButton />
    </div>
  );
}
